// Shared LoginPage - used by BOTH console and admin.
//
// Apps pass a `config` prop to customize:
//   variant      - 'console' or 'admin' (drives theming)
//   title        - heading text
//   subtitle     - sub-heading text
//   ssoButtons   - array of providers to show (['google', 'azure', 'okta'])
//   emailHint    - placeholder + autocomplete value
//   validateEmail(email) - returns null if OK, error string otherwise
//   footerNode   - JSX rendered below the form (e.g. "customer link" or "internal staff" notice)
//   warningNode  - JSX rendered above the divider (admin shows the monitoring warning)
//
// This keeps the entire auth UX in ONE component. When we add Magic Link
// or WebAuthn later, both apps get it at the same time.

import { useState } from 'react';
import { ArrowRight, Mail, Loader2 } from 'lucide-react';
import { signInWithEmail, signInWithGoogle, signInWithAzure } from '../lib/auth.js';
import LogoMark from '../components/primitives/LogoMark.jsx';

const SSO_LABELS = {
  google: 'Continue with Google',
  azure: 'Continue with Microsoft',
  okta: 'Continue with Okta', // Future - SAML via Supabase Enterprise SSO
};

export default function LoginPage({ config }) {
  const {
    variant = 'console',
    title = 'Sign in to your console',
    subtitle = 'Enter your work email to continue.',
    ssoButtons = ['google', 'azure'],
    emailHint = 'you@company.com',
    validateEmail = null,
    footerNode = null,
    warningNode = null,
  } = config || {};

  // Invited users arrive at /login?invite=<token>. Carry that token through
  // the PKCE OAuth round-trip (provider -> /auth/callback -> next) so the
  // invitation is consumed after sign-in. Without this the token is dropped.
  const inviteToken = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '').get('invite');
  const acceptPath = inviteToken ? `/accept-invite?token=${encodeURIComponent(inviteToken)}` : null;
  // Do NOT carry the invite path in the OAuth redirectTo query string: a redirectTo
  // that already has ?next=... gets the provider's ?code=... appended, mangling the
  // round-trip and breaking the PKCE exchange ("Unable to exchange external code").
  // Stash it instead; AuthCallback reads it back after a CLEAN exchange.
  if (inviteToken && typeof window !== 'undefined') {
    try { window.localStorage.setItem('g360_post_login_next', acceptPath); } catch (e) {}
  }
  const ssoRedirect = inviteToken ? `${window.location.origin}/auth/callback` : undefined;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // G360_K644 - an invited person with no Google or Microsoft account (or one
  // who prefers a password) can create a password here. Only with an invite
  // link: there is no open sign-up. Supabase emails a confirmation link to
  // the invited address first, so a copied invite link alone cannot claim
  // someone else's address. The organisation's sign-in rules (Security
  // settings: allowed methods and domains) still apply after sign-in.
  const [mode, setMode] = useState('signin'); // signin | create
  const [password2, setPassword2] = useState('');
  const [notice, setNotice] = useState('');
  // G360_K651 - the code step: a 6-digit code emailed by Govern360 replaces
  // the Supabase confirmation link (which only worked in the same browser).
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');

  const isAdmin = variant === 'admin';
  const primaryBtnClass = isAdmin ? 'btn admin-primary block' : 'btn primary block';

  async function handleEmail(e) {
    e?.preventDefault?.();
    setError('');
    if (validateEmail) {
      const ve = validateEmail(email);
      if (ve) {
        setError(ve);
        return;
      }
    }
    if (!showPassword) {
      // Phase 1: show password field. Don't auto-submit on first click.
      setShowPassword(true);
      return;
    }
    setBusy(true);
    const { error: signInErr } = await signInWithEmail(email, password);
    setBusy(false);
    if (signInErr) {
      setError(signInErr.message || 'Could not sign you in.');
      return;
    }
    if (acceptPath) { window.location.replace(acceptPath); }
    // On success, AuthProvider's onAuthStateChange fires and re-renders the app.
  }

  // G360_K644 / G360_K651 - create a password with an emailed code.
  async function invitePassword(payload) {
    try {
      const r = await fetch('/.netlify/functions/invite-password', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(Object.assign({ token: inviteToken }, payload)) });
      const j = await r.json().catch(() => ({}));
      return r.ok ? Object.assign({ ok: true }, j) : { ok: false, error: j.error || ('Something went wrong (HTTP ' + r.status + '). Try again in a minute.'), expired: !!j.expired };
    } catch (e) { return { ok: false, error: 'Govern360 could not be reached. Check your connection and try again.' }; }
  }
  async function handleCreate(e) {
    e?.preventDefault?.();
    setError(''); setNotice('');
    const em = String(email || '').trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) { setError('Enter the email address your invitation was sent to.'); return; }
    if (validateEmail) { const ve = validateEmail(em); if (ve) { setError(ve); return; } }
    if (String(password).length < 10) { setError('Use at least 10 characters for your password.'); return; }
    if (password !== password2) { setError('The two passwords do not match.'); return; }
    setBusy(true);
    const r = await invitePassword({ step: 'start', email: em });
    setBusy(false);
    if (!r.ok) { setError(r.error); return; }
    setCode(''); setCodeSent(true);
    setNotice('We sent a 6-digit code to ' + em + '. It works for ' + (r.minutes || 15) + ' minutes.');
  }
  async function handleCode(e) {
    e?.preventDefault?.();
    setError('');
    const em = String(email || '').trim().toLowerCase();
    setBusy(true);
    const r = await invitePassword({ step: 'finish', email: em, code: String(code || '').replace(/\D/g, ''), password });
    if (!r.ok) { setBusy(false); setError(r.error); if (r.expired) setCode(''); return; }
    // Password set and email confirmed: sign in here, then finish the invitation.
    const { error: signInErr } = await signInWithEmail(em, password);
    setBusy(false);
    if (signInErr) { setMode('signin'); setShowPassword(true); setCodeSent(false); setNotice(''); setError('Your password is set. Sign in with it below. (' + (signInErr.message || 'sign-in failed') + ')'); return; }
    window.location.replace(acceptPath || '/');
  }
  async function resendCode() {
    setError(''); setBusy(true);
    const r = await invitePassword({ step: 'start', email: String(email || '').trim().toLowerCase() });
    setBusy(false);
    if (!r.ok) { setError(r.error); return; }
    setCode(''); setNotice('A new code is on its way to ' + String(email || '').trim().toLowerCase() + '. Use the newest one.');
  }

  async function handleSso(provider) {
    setError('');
    if (validateEmail && email) {
      const ve = validateEmail(email);
      if (ve) {
        setError(ve);
        return;
      }
    }
    setBusy(true);
    const fn = provider === 'azure' ? signInWithAzure : signInWithGoogle;
    const { error: ssoErr } = await fn(ssoRedirect);
    if (ssoErr) {
      setError(ssoErr.message || 'Could not start sign-in.');
      setBusy(false);
    }
    // On success, browser is redirected to the provider - no further UI needed.
  }

  return (
    <div className="login-wrap">
      <div className="login-card" style={isAdmin ? { borderTop: '4px solid var(--g-admin)' } : undefined}>
        <div className="login-brand">
          <span className="login-logo">
            <LogoMark variant={variant} size={32} />
          </span>
          <span>
            Govern360
            {isAdmin && <span className="login-admin-tag" style={{ marginLeft: 8 }}>Admin</span>}
            {!isAdmin && <span className="brand-ai" style={{ color: 'var(--g-primary)' }}>.ai</span>}
          </span>
        </div>

        <h1 className="login-h1">{inviteToken && mode === 'create' ? 'Join your organisation' : title}{/* G360_K644 */}</h1>
        <p className="login-sub">{subtitle}</p>
        {inviteToken && (
          <div className="banner" style={{ marginBottom: 12 }}>
            {/* G360_K644 */}
            {mode === 'create'
              ? 'You have been invited. Create a password for the email your invite was sent to.'
              : 'You have been invited. Sign in with the email your invite was sent to to join - or create a password if you do not use Google or Microsoft.'}
          </div>
        )}

        {/* G360_K644 - create a password (invite links only) */}
        {inviteToken && mode === 'create' ? (
          codeSent ? (
            // G360_K651 - type the emailed code
            <form onSubmit={handleCode} data-testid="login-code">
              {notice && <div data-testid="login-create-sent" className="banner" style={{ marginBottom: 12, borderLeft: '3px solid var(--g-success)' }}>{notice}</div>}
              <label className="field-l">6-digit code from the email</label>
              <input className="inp" inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9 ]/g, ''))} required data-testid="login-code-input" style={{ fontSize: 20, letterSpacing: 6, textAlign: 'center' }} />
              {error && <p data-testid="login-create-err" style={{ color: 'var(--g-danger)', fontSize: 13, marginTop: 12 }}>{error}</p>}
              <button type="submit" className={primaryBtnClass} style={{ marginTop: 16, height: 44 }} disabled={busy || String(code).replace(/\D/g, '').length !== 6} data-testid="login-code-go">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
                Set password and join
                {!busy && <ArrowRight size={16} />}
              </button>
              <p style={{ fontSize: 12.5, marginTop: 10, textAlign: 'center', color: 'var(--g-text-2)' }}>
                No email? Check spam, or <a href="#resend" data-testid="login-code-resend" onClick={(e) => { e.preventDefault(); if (!busy) resendCode(); }}>send a new code</a>
                {' '}&middot;{' '}<a href="#back" data-testid="login-code-back" onClick={(e) => { e.preventDefault(); setCodeSent(false); setNotice(''); setError(''); }}>change email or password</a>
              </p>
            </form>
          ) : (
          <form onSubmit={handleCreate} data-testid="login-create">
            <label className="field-l">Work email</label>
            <input className="inp" type="email" placeholder={emailHint} value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required data-testid="login-create-email" />
            <div style={{ marginTop: 12 }}>
              <label className="field-l">Create a password</label>
              <input className="inp" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" required data-testid="login-create-pw" />
              <div style={{ fontSize: 12, color: 'var(--g-text-3)', marginTop: 4 }}>At least 10 characters. Next, we email you a 6-digit code to confirm the address.</div>
            </div>
            <div style={{ marginTop: 12 }}>
              <label className="field-l">Confirm password</label>
              <input className="inp" type="password" value={password2} onChange={(e) => setPassword2(e.target.value)} autoComplete="new-password" required data-testid="login-create-pw2" />
            </div>
            {error && <p data-testid="login-create-err" style={{ color: 'var(--g-danger)', fontSize: 13, marginTop: 12 }}>{error}</p>}
            <button type="submit" className={primaryBtnClass} style={{ marginTop: 16, height: 44 }} disabled={busy} data-testid="login-create-go">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
              Email me a code
              {!busy && <ArrowRight size={16} />}
            </button>
          </form>
          )
        ) : (
        <form onSubmit={handleEmail}>
          <label className="field-l">Work email</label>
          <input
            className="inp"
            type="email"
            placeholder={emailHint}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />

          {showPassword && (
            <div style={{ marginTop: 12 }}>
              <label className="field-l">Password</label>
              <input
                className="inp"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
          )}

          {error && (
            <p style={{ color: 'var(--g-danger)', fontSize: 13, marginTop: 12 }}>{error}</p>
          )}

          <button
            type="submit"
            className={primaryBtnClass}
            style={{ marginTop: 16, height: 44 }}
            disabled={busy}
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
            {showPassword ? 'Sign in' : 'Continue with email'}
            {!busy && <ArrowRight size={16} />}
          </button>
        </form>
        )}
        {/* G360_K644 - switch between signing in and creating a password */}
        {inviteToken && (
          <p style={{ fontSize: 13, marginTop: 12, textAlign: 'center' }}>
            {mode === 'create'
              ? <>Already have a password? <a href="#signin" data-testid="login-to-signin" onClick={(e) => { e.preventDefault(); setMode('signin'); setError(''); setNotice(''); setCodeSent(false); }}>Sign in</a></>
              : <>New to Govern360 and not using Google or Microsoft? <a href="#create" data-testid="login-to-create" onClick={(e) => { e.preventDefault(); setMode('create'); setError(''); setNotice(''); setCodeSent(false); }}>Create a password</a></>}
          </p>
        )}

        {ssoButtons.length > 0 && (
          <>
            <div className="login-divider"><span>or</span></div>
            <div className="login-sso">
              {ssoButtons.map((provider) => (
                <button
                  key={provider}
                  type="button"
                  className="btn outline block"
                  onClick={() => handleSso(provider)}
                  disabled={busy}
                >
                  {SSO_LABELS[provider] || provider}
                </button>
              ))}
            </div>
          </>
        )}

        {warningNode}
        {footerNode && <p className="login-foot">{footerNode}</p>}
      </div>
    </div>
  );
}
